import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { hasValidStoredAccessToken, login } from '../../../api';
import { getPostLoginRedirect } from '../../../utils/authRedirect';

const REMEMBER_ME_KEY = 'rememberMe';
const REMEMBERED_EMAIL_KEY = 'email';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = useMemo(() => getPostLoginRedirect(location.state?.from), [location.state]);
  const [form, setForm] = useState({ email: '', password: '' });
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (hasValidStoredAccessToken()) {
      navigate(getPostLoginRedirect(location.state?.from), { replace: true });
      return;
    }

    if (localStorage.getItem(REMEMBER_ME_KEY) === 'true') {
      setRememberMe(true);
      setForm((current) => ({
        ...current,
        email: localStorage.getItem(REMEMBERED_EMAIL_KEY) || '',
      }));
    }
  }, [navigate, location.state]);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const email = form.email.trim();
    const password = form.password;

    if (!email || !password) {
      setError('Email and password are required');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await login({ email, password });
      if (rememberMe) {
        localStorage.setItem(REMEMBER_ME_KEY, 'true');
        localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
      } else {
        localStorage.removeItem(REMEMBER_ME_KEY);
        localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      }
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err?.message || 'An error occurred during login');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="login-shell">
      <div className="login-orb login-orb-one" />
      <div className="login-orb login-orb-two" />
      <div className="login-orb login-orb-three" />

      <section className="login-card" aria-label="Login">
        <div className="login-brand">
          <div className="login-brand-mark">IA</div>
          <div>
            <div className="login-brand-name">Intel Axis</div>
            <div className="login-brand-subtitle">Powered by Comply</div>
          </div>
        </div>

        <div className="login-copy">
          <p className="login-kicker">Market intelligence workspace</p>
          {/* <h1>Welcome back</h1>
          <p>Log in to continue monitoring competitors, filings, objections, and AI research.</p> */}
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <label className="login-field" htmlFor="email">
            <span>Email address</span>
            <div className="login-input-wrap">
              <Mail size={17} aria-hidden="true" />
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="Enter your email"
                value={form.email}
                onChange={(event) => updateField('email', event.target.value)}
                disabled={isLoading}
                required
              />
            </div>
          </label>

          <label className="login-field" htmlFor="password">
            <span>Password</span>
            <div className="login-input-wrap">
              <Lock size={17} aria-hidden="true" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Enter your password"
                value={form.password}
                onChange={(event) => updateField('password', event.target.value)}
                disabled={isLoading}
                required
              />
              <button
                type="button"
                className="login-password-toggle"
                onClick={() => setShowPassword((current) => !current)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                disabled={isLoading}
              >
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </label>

          <div className="login-form-row">
            <label className="login-remember">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(event) => setRememberMe(event.target.checked)}
                disabled={isLoading}
              />
              <span>Remember email</span>
            </label>
          </div>

          {error && <div className="login-error" role="alert">{error}</div>}

          <button className="login-submit" type="submit" disabled={isLoading}>
            <span>{isLoading ? 'Signing in...' : 'Sign in'}</span>
            <ArrowRight size={17} />
          </button>
        </form>
      </section>
    </main>
  );
};

export default Login;
